import { supabase } from "./core.js";

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface SnapshotRow {
  patrimonio_total: number;
  period: string;
  ref_date: string;
  data_snapshot: string; // alias de ref_date (select usa data_snapshot:ref_date)
  suitability_status: string | null;
  client_id: string;
}

interface TransactionRow {
  data_transacao: string;
  tipo_transacao: string;
  ativo_simbolo: string | null;
  ativo_nome: string;
  valor: number;
  quantidade: number;
  descricao: string;
  cliente_id: string;
}

interface DriftRow {
  percentual_drift: number;
  data_analise: string;
  cliente_id: string;
}

interface CrmEventRow {
  tipo_evento: string;
  descricao: string;
  data_evento: string;
  cliente_id: string;
}

interface ClientRow {
  id: string;
  nome: string;
  email: string;
  telefone: string;
  patrimonio_total: number;
}

interface PerformanceResult {
  totalReturn: number;
  monthlyAvgReturn: number;
  snapshotCount: number;
  startDate?: string;
  endDate?: string;
  startPatrimonio?: number;
  endPatrimonio?: number;
}

interface Movement {
  data: string;
  tipo: string;
  ativo: string;
  valor: number;
  quantidade: number;
  descricao: string;
}

interface Alert {
  type: string;
  severity: string;
  message: string;
  description: string;
  date?: string;
}

interface AgendaItem {
  item: string;
  duration: string;
  description: string;
}

interface ReviewPrepResult {
  client: {
    id: string;
    nome: string;
    email: string;
    telefone: string;
    patrimonio_total: number;
  };
  performance: PerformanceResult;
  movements: Movement[];
  alerts: Alert[];
  agenda: AgendaItem[];
  generatedAt: string;
}

// ============================================================================
// MAIN EXPORT
// ============================================================================

export async function generateReviewPrep(clientId: string): Promise<ReviewPrepResult> {
  try {
    // Fetch latest snapshot
    // NOTA: coluna real é `ref_date` (data_snapshot não existe na tabela).
    // Usa alias `data_snapshot:ref_date` para manter o resto do código compatível.
    const { data: latestSnapshot, error: snapError } = await supabase
      .from("snapshots")
      .select(
        "patrimonio_total, period, ref_date, data_snapshot:ref_date, suitability_status, client_id"
      )
      .eq("client_id", clientId)
      .order("ref_date", { ascending: false })
      .limit(1)
      .single();

    if (snapError) throw new Error(`Error fetching latest snapshot: ${snapError.message}`);

    // Fetch snapshot history (last 6 months)
    const { data: snapshotHistory, error: histError } = await supabase
      .from("snapshots")
      .select(
        "patrimonio_total, period, ref_date, data_snapshot:ref_date, suitability_status, client_id"
      )
      .eq("client_id", clientId)
      .order("ref_date", { ascending: false })
      .limit(6);

    if (histError) throw new Error(`Error fetching snapshot history: ${histError.message}`);

    // Fetch transactions (last 90 days)
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);

    const { data: transactions, error: txError } = await supabase
      .from("transactions")
      .select(
        "data_transacao, tipo_transacao, ativo_simbolo, ativo_nome, valor, quantidade, descricao, cliente_id"
      )
      .eq("cliente_id", clientId)
      .gte("data_transacao", ninetyDaysAgo.toISOString())
      .order("data_transacao", { ascending: false });

    if (txError) throw new Error(`Error fetching transactions: ${txError.message}`);

    // Fetch drift data
    const { data: driftData, error: _driftError } = await supabase
      .from("drift_analysis")
      .select("percentual_drift, data_analise, cliente_id")
      .eq("cliente_id", clientId)
      .order("data_analise", { ascending: false })
      .limit(1)
      .single();

    // Fetch CRM events
    const { data: crmEvents, error: crmError } = await supabase
      .from("crm_events")
      .select("tipo_evento, descricao, data_evento, cliente_id")
      .eq("cliente_id", clientId)
      .order("data_evento", { ascending: false })
      .limit(5);

    if (crmError && crmError.code !== "PGRST116") {
      throw new Error(`Error fetching CRM events: ${crmError.message}`);
    }

    // Fetch client info
    const { data: clientData, error: clientError } = await supabase
      .from("clientes")
      .select("id, nome, email, telefone, patrimonio_total")
      .eq("id", clientId)
      .single();

    if (clientError) throw new Error(`Error fetching client: ${clientError.message}`);

    // Calculate performance since last review
    const performance = calculatePerformance(snapshotHistory, latestSnapshot);

    // Get top 5 movements
    const movements = getTopMovements(transactions);

    // Get drift alerts
    const alerts = generateAlerts(driftData, latestSnapshot, crmEvents);

    // Generate suggested agenda
    const agenda = generateAgenda(performance, movements, alerts, latestSnapshot);

    const generatedAt = new Date().toISOString();

    return {
      client: {
        id: clientData.id,
        nome: clientData.nome,
        email: clientData.email,
        telefone: clientData.telefone,
        patrimonio_total: latestSnapshot?.patrimonio_total || 0,
      },
      performance,
      movements,
      alerts,
      agenda,
      generatedAt,
    };
  } catch (error) {
    console.error("Error generating review prep:", error);
    throw error;
  }
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function calculatePerformance(
  snapshotHistory: SnapshotRow[] | null,
  _latestSnapshot: SnapshotRow | null
): PerformanceResult {
  if (!snapshotHistory || snapshotHistory.length === 0) {
    return {
      totalReturn: 0,
      monthlyAvgReturn: 0,
      snapshotCount: 0,
    };
  }

  const oldest = snapshotHistory[snapshotHistory.length - 1];
  const newest = snapshotHistory[0];

  const totalReturn =
    ((newest.patrimonio_total - oldest.patrimonio_total) / oldest.patrimonio_total) * 100 || 0;
  const monthlyAvgReturn = totalReturn / snapshotHistory.length;

  return {
    totalReturn: parseFloat(totalReturn.toFixed(2)),
    monthlyAvgReturn: parseFloat(monthlyAvgReturn.toFixed(2)),
    snapshotCount: snapshotHistory.length,
    startDate: oldest.data_snapshot,
    endDate: newest.data_snapshot,
    startPatrimonio: oldest.patrimonio_total,
    endPatrimonio: newest.patrimonio_total,
  };
}

function getTopMovements(transactions: TransactionRow[] | null): Movement[] {
  if (!transactions || transactions.length === 0) {
    return [];
  }

  return transactions
    .sort((a: TransactionRow, b: TransactionRow) => Math.abs(b.valor || 0) - Math.abs(a.valor || 0))
    .slice(0, 5)
    .map(
      (tx: TransactionRow): Movement => ({
        data: tx.data_transacao,
        tipo: tx.tipo_transacao,
        ativo: tx.ativo_simbolo || tx.ativo_nome,
        valor: tx.valor,
        quantidade: tx.quantidade,
        descricao: tx.descricao,
      })
    );
}

function generateAlerts(
  driftData: DriftRow | null,
  latestSnapshot: SnapshotRow | null,
  crmEvents: CrmEventRow[] | null
): Alert[] {
  const alerts: Alert[] = [];

  // Drift alert
  if (driftData && driftData.percentual_drift > 10) {
    alerts.push({
      type: "drift",
      severity: driftData.percentual_drift > 20 ? "high" : "medium",
      message: `Desvio de alocação: ${driftData.percentual_drift.toFixed(1)}%`,
      description: `Portfolio desviou ${driftData.percentual_drift.toFixed(1)}% da alocação recomendada`,
    });
  }

  // Suitability alert
  if (latestSnapshot?.suitability_status && latestSnapshot.suitability_status !== "OK") {
    alerts.push({
      type: "suitability",
      severity: "high",
      message: `Suitability: ${latestSnapshot.suitability_status}`,
      description: "Portfolio pode não estar alinhado com perfil de risco do cliente",
    });
  }

  // CRM events as alerts
  if (crmEvents && crmEvents.length > 0) {
    crmEvents.forEach((event: CrmEventRow): void => {
      if (event.tipo_evento === "concern" || event.tipo_evento === "complaint") {
        alerts.push({
          type: "crm",
          severity: event.tipo_evento === "complaint" ? "high" : "medium",
          message: `${event.tipo_evento.charAt(0).toUpperCase() + event.tipo_evento.slice(1)}: ${event.descricao}`,
          description: event.descricao,
          date: event.data_evento,
        });
      }
    });
  }

  return alerts;
}

function generateAgenda(
  performance: PerformanceResult,
  movements: Movement[],
  alerts: Alert[],
  _latestSnapshot: SnapshotRow | null
): AgendaItem[] {
  const agenda: AgendaItem[] = [];

  // Performance review
  agenda.push({
    item: "Análise de Desempenho",
    duration: "10 min",
    description: `Retorno total: ${performance.totalReturn.toFixed(2)}% | Média mensal: ${performance.monthlyAvgReturn.toFixed(2)}%`,
  });

  // Movements
  if (movements.length > 0) {
    agenda.push({
      item: "Movimentações Recentes",
      duration: "5 min",
      description: `${movements.length} movimentação(ões) principal(is) para discutir`,
    });
  }

  // Alerts
  if (alerts.length > 0) {
    agenda.push({
      item: "Pontos de Atenção",
      duration: "10 min",
      description: `${alerts.length} alerta(s) para revisar`,
    });
  }

  // Rebalancing if drift
  const highDriftAlert = alerts.find(
    (a: Alert): boolean => a.type === "drift" && a.severity === "high"
  );
  if (highDriftAlert) {
    agenda.push({
      item: "Rebalanceamento Sugerido",
      duration: "10 min",
      description: "Discussão e aprovação de rebalanceamento para alinhar com alto",
    });
  }

  // General review
  agenda.push({
    item: "Revisão de Objetivos e Estratégia",
    duration: "15 min",
    description: "Validação de objetivos financeiros e horizonte de investimento",
  });

  return agenda;
}

export default generateReviewPrep;
